import React, { useEffect, useState, useCallback } from 'react'
import { Table, Tag, Button, Drawer, Form, Input, message, Progress, Empty, Segmented } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import styled, { keyframes, css } from 'styled-components'
import {
  getAssignmentStatus,
  getAssignmentItems,
  getAssignmentByGroup,
  retryAssignment,
  getCalcStatus,
  getCalcItems,
  retryCalc,
} from '../../services/assignmentActivityAPI'

/* ───────── Theme tokens ───────── */
const T = {
  primary: '#3249D7',
  textPrimary: '#242E39',
  textSecondary: '#637487',
  border: '#EBECEE',
  green: '#4cb187',
  red: '#E5484D',
  amber: '#B5641E',
  grey95: '#F2F3F5',
}

/* ───────── Animations (mirrors AssessmentSetReview) ───────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`
const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1.2); }
`
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
`
const countUp = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`

const Wrap = styled.div`
  padding: ${(p) => (p.$embedded ? '4px 0 6px' : '4px')};
  animation: ${fadeIn} 0.3s ease-out;
`

/* Stat cards — same grid/card/accent language as the set-generation view */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 18px;
  @media (max-width: 720px) { grid-template-columns: repeat(2, 1fr); }
`
const StatCard = styled.div`
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 10px;
  padding: 18px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideDown} 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${(p) => p.$delay || '0s'} both;
  &:hover {
    border-color: ${(p) => p.$accent || T.primary};
    box-shadow: 0 2px 12px ${(p) => p.$accent || T.primary}18;
  }
  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: ${(p) => p.$accent || T.primary};
    border-radius: 10px 10px 0 0;
  }
`
const StatValue = styled.div`
  font-size: 28px; font-weight: 700; line-height: 1; margin-bottom: 6px;
  color: ${(p) => p.$color || T.textPrimary};
  animation: ${countUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${(p) => p.$delay || '0s'} both;
`
const StatLabel = styled.div`
  font-size: 12px; font-weight: 600; color: ${T.textSecondary};
  text-transform: uppercase; letter-spacing: 0.4px;
`

/* Progress card with live indicator */
const ProgressCard = styled.div`
  padding: 18px 22px;
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 10px;
  box-shadow: 0px 1px 4px rgba(99, 116, 135, 0.08);
  margin-bottom: 18px;
  animation: ${fadeIn} 0.3s ease-out 0.1s both;
`
const ProgressTop = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
`
/* Set-preparation phase: shown before any student work starts, so the job does
   not read as a stalled 0% while an LLM + TTS generation is still running. */
const PrepareMeta = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: ${T.textSecondary};
  display: flex; flex-wrap: wrap; gap: 6px 14px;
`
const PrepareTag = styled.span`
  font-weight: 600;
  color: ${T.textPrimary};
`
const StateLabel = styled.span`
  font-size: 13px; font-weight: 600; text-transform: capitalize;
  color: ${(p) => p.$color || T.textSecondary};
  display: inline-flex; align-items: center; gap: 8px;
  ${(p) => p.$live && css`animation: ${pulse} 1.8s ease-in-out infinite;`}
`
const LiveDots = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
`
const Dot = styled.span`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${(p) => p.$color || T.primary};
  animation: ${dotPulse} 1.4s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || '0s'};
`

/* Loading + table card */
const LoadingWrapper = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: ${(p) => (p.$embedded ? '180px' : '320px')};
`
const LoadingSpinner = styled.div`
  width: 56px; height: 56px; border-radius: 14px; background: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0px 2px 10px rgba(99, 116, 135, 0.2); margin-bottom: 18px;
  .anticon { font-size: 24px; color: ${T.primary}; }
`
const LoadingText = styled.div`font-size: 14px; font-weight: 500; color: ${T.textSecondary};`
const SkeletonLine = styled.div`
  height: ${(p) => p.$h || '12px'}; width: ${(p) => p.$w || '100%'};
  border-radius: 6px; margin: 0 auto 10px;
  background: linear-gradient(90deg, #F0F2F4 25%, #F8F9FA 50%, #F0F2F4 75%);
  background-size: 400px 100%; animation: ${shimmer} 1.6s ease-in-out infinite;
`
const TableCard = styled.div`
  background: #fff; border: 1px solid ${T.border}; border-radius: 10px; overflow: hidden;
  box-shadow: 0px 1px 4px rgba(99, 116, 135, 0.06);
  animation: ${fadeIn} 0.3s ease-out 0.15s both;
  .ant-table-thead > tr > th {
    background: #FAFBFC; color: ${T.textSecondary}; font-size: 11px; font-weight: 700;
    letter-spacing: 0.4px; text-transform: uppercase; border-bottom: 1px solid ${T.border};
  }
  .ant-table-tbody > tr > td { padding: 12px 16px; }
  .ant-table-tbody > tr { animation: ${fadeIn} 0.3s ease-out both; }
`
const Toolbar = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap;`
const Reason = styled.span`color: ${T.textSecondary}; font-size: 12px;`

// Data errors (need a fix before retry) vs transient (just retry).
const DATA_ERROR = /already\s*exist|duplicate|registered|invalid|validation|not found|missing|required/i
const classify = (lastError) => (DATA_ERROR.test(lastError || '') ? 'data' : 'transient')

const EDITABLE = [
  ['firstName', 'First name', (p) => p?.admin?.firstName],
  ['lastName', 'Last name', (p) => p?.admin?.lastName],
  ['email', 'Email', (p) => p?.admin?.email],
  ['degree', 'Degree', (p) => p?.currentCourse?.degree],
  ['department', 'Department / Stream', (p) => p?.currentCourse?.department],
  ['specialisation', 'Specialisation', (p) => p?.currentCourse?.specialisation],
]

const TERMINAL = ['completed', 'completed_with_errors', 'failed']
const STATUS_TAG = (s) => {
  if (s === 'failed') return <Tag color={T.red}>failed</Tag>
  if (s === 'done') return <Tag color={T.green}>done</Tag>
  if (s === 'pending') return <Tag>pending</Tag>
  return <Tag color="processing">{s}</Tag>
}

/**
 * Assignment Activity — progress + per-student table + retry for one job.
 * Props: { jobId } OR { groupId } (resolved to a job), { embedded } (inline in a
 * Manage-Assessments expandable row). The per-student table is SERVER-paginated
 * (status filter + limit/offset) so it scales to 1000+ students.
 */
const AssignmentActivity = ({ jobId: propJobId, groupId, embedded = false }) => {
  const [jobId, setJobId] = useState(propJobId || null)
  const [resolving, setResolving] = useState(!propJobId && !!groupId)
  const [mode, setMode] = useState('assignment')        // assignment | calculation
  const [status, setStatus] = useState(null)            // counts/state snapshot
  const [items, setItems] = useState([])                // current page of students
  const [total, setTotal] = useState(0)
  const [filter, setFilter] = useState('failed')        // failed | pending | done | all
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [loading, setLoading] = useState(true)
  const [retrying, setRetrying] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [form] = Form.useForm()

  // Resolve groupId → jobId once.
  useEffect(() => {
    let alive = true
    if (propJobId) { setJobId(propJobId); return undefined }
    if (!groupId) return undefined
    setResolving(true)
    getAssignmentByGroup(groupId)
      .then((res) => { if (alive) setJobId(res.data?.data?.jobId || null) })
      .catch(() => { if (alive) setJobId(null) })
      .finally(() => { if (alive) setResolving(false) })
    return () => { alive = false }
  }, [propJobId, groupId])

  const load = useCallback(async () => {
    if (!jobId) { setLoading(false); return }
    try {
      const opts = { status: filter, limit: pageSize, offset: (page - 1) * pageSize }
      const [s, it] = await Promise.all([
        mode === 'calculation' ? getCalcStatus(jobId) : getAssignmentStatus(jobId),
        mode === 'calculation' ? getCalcItems(jobId, opts) : getAssignmentItems(jobId, opts),
      ])
      setStatus(s.data?.data || null)
      setItems(it.data?.data?.items || [])
      setTotal(it.data?.data?.total || 0)
    } catch (err) {
      message.error(`Failed to load ${mode === 'calculation' ? 'calculation' : 'assignment'} activity`)
    }
    setLoading(false)
  }, [jobId, mode, filter, page, pageSize])

  useEffect(() => { load() }, [load])

  // Live-poll counts + current page until the work is settled.
  // Assignment: until the job state is terminal. Calculation: while anything is
  // still scoring (pending or in-process) — scoring trickles in as students submit.
  const calcLive = mode === 'calculation' && status && ((status.pendingCalc || 0) > 0 || (status.processing || 0) > 0)
  const assignLive = mode === 'assignment' && !(status && TERMINAL.includes(status.state))
  const isPolling = !!jobId && (mode === 'calculation' ? calcLive : assignLive)
  useEffect(() => {
    if (!isPolling) return undefined
    const id = setInterval(load, 2500)
    return () => clearInterval(id)
  }, [isPolling, load])

  // Switch mode → reset paging/filter and clear the stale snapshot.
  const switchMode = (m) => {
    if (m === mode) return
    setMode(m); setFilter('failed'); setPage(1); setStatus(null); setItems([]); setTotal(0); setLoading(true)
  }

  const doRetry = async (retryItems, label) => {
    setRetrying(true)
    try {
      let n
      if (mode === 'calculation') {
        const ids = Array.isArray(retryItems) ? retryItems.map((i) => i.id).filter(Boolean) : null
        const res = await retryCalc(jobId, ids)
        n = res.data?.data?.retried ?? 0
      } else {
        const res = await retryAssignment(jobId, retryItems)
        n = res.data?.data?.retried ?? 0
      }
      message.success(`${label}: ${n} ${mode === 'calculation' ? 'assessment(s) re-queued for scoring' : 'item(s) re-queued'}`)
      setEditItem(null)
      setTimeout(load, 1200)
    } catch (err) {
      message.error('Retry failed')
    }
    setRetrying(false)
  }

  const openEdit = (record) => {
    const p = record.studentPayload || {}
    const init = {}
    EDITABLE.forEach(([key, , get]) => { init[key] = get(p) || '' })
    form.setFieldsValue(init)
    setEditItem(record)
  }

  const saveEdit = async () => {
    const vals = await form.validateFields()
    const p = JSON.parse(JSON.stringify(editItem.studentPayload || {}))
    p.admin = p.admin || {}; p.currentCourse = p.currentCourse || {}
    p.admin.firstName = vals.firstName; p.admin.lastName = vals.lastName; p.admin.email = vals.email
    p.currentCourse.degree = vals.degree; p.currentCourse.department = vals.department; p.currentCourse.specialisation = vals.specialisation
    await doRetry([{ itemId: editItem.itemId, studentPayload: p }], 'Edited & retried')
  }

  const columns = [
    { title: 'STUDENT', dataIndex: 'primaryEmail', key: 'email',
      render: (v) => <span style={{ fontWeight: 600, color: T.textPrimary }}>{v}</span> },
    { title: 'TYPE', dataIndex: 'category', key: 'category', width: 110,
      render: (c) => <Tag>{c === 'new' ? 'New' : c === 'existing_first_time' ? 'First-time' : 'Existing'}</Tag> },
    { title: 'STATUS', dataIndex: 'status', key: 'status', width: 110, render: STATUS_TAG },
    { title: 'REASON', dataIndex: 'lastError', key: 'reason',
      render: (e) => (e ? <Reason title={e}>{e.slice(0, 90)}</Reason> : <span style={{ color: T.textSecondary }}>—</span>) },
    { title: 'ATTEMPTS', dataIndex: 'attempts', key: 'attempts', width: 90 },
    { title: '', key: 'action', width: 150,
      render: (_, r) => (r.status !== 'failed' ? null
        : classify(r.lastError) === 'data'
          ? <Button size="small" onClick={() => openEdit(r)}>Edit &amp; retry</Button>
          : <Button size="small" onClick={() => doRetry([{ itemId: r.itemId }], 'Retried')}>Retry</Button>) },
  ]

  // Calculation (scoring) columns — scoring is post-submit, per submitted assessment.
  const calcColumns = [
    { title: 'STUDENT', dataIndex: 'primaryEmail', key: 'email',
      render: (v) => <span style={{ fontWeight: 600, color: T.textPrimary }}>{v}</span> },
    { title: 'SCORING', dataIndex: 'calcStatus', key: 'calcStatus', width: 130, render: STATUS_TAG },
    // ATTEMPTS = failed-retry counter (reset to 0 on success). Meaningful in the
    // Failed view and the Pending view (a pending row with attempts > 0 is a
    // stuck/released row); hidden in Done/All to avoid a confusing 0.
    ...(filter === 'failed' || filter === 'pending' ? [{ title: 'ATTEMPTS', dataIndex: 'attempts', key: 'attempts', width: 100 }] : []),
    { title: 'SUBMITTED', dataIndex: 'submittedAt', key: 'submittedAt', width: 190,
      render: (d) => (d ? <span style={{ color: T.textSecondary, fontSize: 12 }}>{new Date(d).toLocaleString()}</span> : <span style={{ color: T.textSecondary }}>—</span>) },
    { title: '', key: 'action', width: 150,
      // Show Retry for terminal-failed rows AND for stuck-pending rows that have
      // already tried at least once (attempts > 0) — a released-pending row can
      // wedge without ever reaching 'failed', and the operator needs to recover
      // it. A fresh, never-tried pending row (attempts 0) is left alone.
      render: (_, r) => ((r.calcStatus === 'failed' || (r.calcStatus === 'pending' && r.attempts > 0))
        ? <Button size="small" onClick={() => doRetry([{ id: r.id }], 'Retried scoring')}>Retry scoring</Button>
        : null) },
  ]

  if (resolving || (loading && !status)) {
    return (
      <LoadingWrapper $embedded={embedded}>
        <LoadingSpinner><LoadingOutlined spin /></LoadingSpinner>
        <LoadingText>Loading {mode === 'calculation' ? 'calculation' : 'assignment'} activity…</LoadingText>
        <div style={{ width: 220, marginTop: 22 }}>
          <SkeletonLine $w="70%" $h="14px" />
          <SkeletonLine $w="100%" />
          <SkeletonLine $w="85%" />
        </div>
      </LoadingWrapper>
    )
  }
  if (!jobId) return <Empty description="No assignment activity for this assessment yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
  if (!status) return <Empty description="No activity" />

  const isCalc = mode === 'calculation'
  // Assignment progress = assigned / total. Calculation progress = scored / submitted.
  const denom = isCalc ? (status.submitted || 0) : (status.total || 0)
  const numer = isCalc ? (status.scored || 0) : (status.assigned || 0)
  const pct = denom ? Math.round((numer / denom) * 100) : 0
  const failedCount = isCalc ? (status.failedCalc || 0) : (status.failed || 0)
  const isLive = isCalc
    ? ((status.pendingCalc || 0) > 0 || (status.processing || 0) > 0)
    : !TERMINAL.includes(status.state)
  const stateColor = isCalc
    ? (failedCount ? T.red : isLive ? T.primary : T.green)
    : (status.state === 'completed' ? T.green
      : status.state === 'completed_with_errors' ? T.amber
      : status.state === 'failed' ? T.red : T.primary)
  const stateText = isCalc
    ? (isLive ? 'scoring in progress' : failedCount ? 'completed with errors' : 'all scored')
    : ((status.state || '').replace(/_/g, ' ') || 'processing')

  // Set preparation runs BEFORE any item is dispatched, so every student counter
  // is legitimately 0 while it does. Surface it as its own phase rather than
  // letting the job look stuck at 0%. `prepare` is null when the job needed no
  // preparation at all, so this whole block disappears for pool-picked assigns.
  const prepare = isCalc ? null : status.prepare
  const isPreparing = Boolean(prepare && status.state === 'preparing')
  const preparePct = prepare && prepare.total ? Math.round((prepare.ready / prepare.total) * 100) : 0

  return (
    <Wrap $embedded={embedded}>
      <Toolbar style={{ marginBottom: 16 }}>
        <Segmented
          value={mode}
          onChange={switchMode}
          options={[
            { label: 'Assignment', value: 'assignment' },
            { label: 'Calculation', value: 'calculation' },
          ]}
        />
      </Toolbar>

      {isPreparing && (
        <ProgressCard>
          <ProgressTop>
            <StateLabel $color={T.primary} $live>
              <LiveDots>
                <Dot $color={T.primary} $delay="0s" />
                <Dot $color={T.primary} $delay="0.2s" />
                <Dot $color={T.primary} $delay="0.4s" />
              </LiveDots>
              {prepare.isGenerating ? 'Generating question sets' : 'Preparing question sets'}
            </StateLabel>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>
              {prepare.ready}/{prepare.total}
            </span>
          </ProgressTop>
          <Progress percent={preparePct} strokeColor={T.primary} showInfo={false} status="active" />
          {prepare.isGenerating && (
            <PrepareMeta>
              {prepare.topic && <span>Topic <PrepareTag>{prepare.topic}</PrepareTag></span>}
              {prepare.accent && <span>Accent <PrepareTag>{prepare.accent}</PrepareTag></span>}
              <span>Students are invited once the sets are ready.</span>
            </PrepareMeta>
          )}
        </ProgressCard>
      )}

      <StatsGrid>
        {isCalc ? (
          <>
            <StatCard $accent={T.primary} $delay="0s">
              <StatValue $delay="0s">{status.submitted}</StatValue>
              <StatLabel>Submitted</StatLabel>
            </StatCard>
            <StatCard $accent={T.green} $delay="0.05s">
              <StatValue $color={T.green} $delay="0.05s">{status.scored}</StatValue>
              <StatLabel>Scored</StatLabel>
            </StatCard>
            <StatCard $accent={T.primary} $delay="0.1s">
              <StatValue $color={T.primary} $delay="0.1s">{status.pendingCalc}</StatValue>
              <StatLabel>Pending</StatLabel>
            </StatCard>
            <StatCard $accent={status.failedCalc ? T.red : T.textSecondary} $delay="0.15s">
              <StatValue $color={status.failedCalc ? T.red : T.textPrimary} $delay="0.15s">{status.failedCalc}</StatValue>
              <StatLabel>Failed</StatLabel>
            </StatCard>
          </>
        ) : (
          <>
            <StatCard $accent={T.primary} $delay="0s">
              <StatValue $delay="0s">{status.total}</StatValue>
              <StatLabel>Total</StatLabel>
            </StatCard>
            <StatCard $accent={T.primary} $delay="0.05s">
              <StatValue $color={T.primary} $delay="0.05s">{status.assigned}</StatValue>
              <StatLabel>Assigned</StatLabel>
            </StatCard>
            <StatCard $accent={T.green} $delay="0.1s">
              <StatValue $color={T.green} $delay="0.1s">{status.emailed}</StatValue>
              <StatLabel>Notified</StatLabel>
            </StatCard>
            <StatCard $accent={status.failed ? T.red : T.textSecondary} $delay="0.15s">
              <StatValue $color={status.failed ? T.red : T.textPrimary} $delay="0.15s">{status.failed}</StatValue>
              <StatLabel>Failed</StatLabel>
            </StatCard>
          </>
        )}
      </StatsGrid>

      <ProgressCard>
        <ProgressTop>
          <StateLabel $color={stateColor} $live={isLive}>
            {isLive && (
              <LiveDots>
                <Dot $color={stateColor} $delay="0s" />
                <Dot $color={stateColor} $delay="0.2s" />
                <Dot $color={stateColor} $delay="0.4s" />
              </LiveDots>
            )}
            {stateText}
          </StateLabel>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{pct}%</span>
        </ProgressTop>
        <Progress percent={pct} strokeColor={stateColor} showInfo={false} status={isLive ? 'active' : 'normal'} />
      </ProgressCard>

      <Toolbar>
        <Segmented
          size="small"
          value={filter}
          onChange={(v) => { setFilter(v); setPage(1) }}
          options={[
            { label: `Failed${failedCount ? ` (${failedCount})` : ''}`, value: 'failed' },
            { label: 'Pending', value: 'pending' },
            { label: 'Done', value: 'done' },
            { label: 'All', value: 'all' },
          ]}
        />
        {filter === 'failed' && total > 0 && (
          <Button danger loading={retrying} onClick={() => doRetry(null, isCalc ? 'Re-queued all failed scoring' : 'Retried all transient')}>Retry all</Button>
        )}
      </Toolbar>

      {total === 0
        ? <Empty style={{ padding: '48px 0' }} description={filter === 'failed'
            ? (isCalc ? 'No scoring failures 🎉' : 'No failures 🎉')
            : (isCalc ? 'No submitted assessments in this view' : 'No students in this view')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        : <TableCard>
            <Table
              rowKey={isCalc ? 'id' : 'itemId'}
              columns={isCalc ? calcColumns : columns}
              dataSource={items}
              size="middle"
              loading={loading}
              pagination={{ current: page, pageSize, total, showSizeChanger: false, onChange: setPage, showTotal: (t) => `${t} student${t === 1 ? '' : 's'}` }}
            />
          </TableCard>}

      <Drawer
        title="Edit student & retry"
        open={!!editItem}
        onClose={() => setEditItem(null)}
        width={420}
        extra={<Button type="primary" loading={retrying} onClick={saveEdit} style={{ background: T.primary, borderColor: T.primary }}>Save &amp; retry</Button>}
      >
        <Form form={form} layout="vertical">
          {EDITABLE.map(([key, label]) => (
            <Form.Item key={key} name={key} label={label}
              rules={key === 'email' ? [{ type: 'email', message: 'Enter a valid email' }] : []}>
              <Input />
            </Form.Item>
          ))}
        </Form>
      </Drawer>
    </Wrap>
  )
}

export default AssignmentActivity
